import React, { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { ArrowDownToLine } from "lucide-react";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";

/**
 * Renders a full-screen interstitial when a soft update is available, and a
 * blocking modal when the user is below the minimum supported version.
 *
 * Mount once near the root of the app. Renders nothing on web or when the
 * version check has not flagged an update.
 */

const openStore = async (url: string) => {
  if (Capacitor.isNativePlatform()) {
    try {
      await Browser.open({ url });
      return;
    } catch {
      // Fall through to window.open below.
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

export const AppUpdateGate: React.FC = () => {
  const {
    status,
    latestVersion,
    currentVersion,
    releaseNotes,
    storeUrl,
  } = useAppVersionCheck();
  const [dismissed, setDismissed] = useState(false);

  if (status === "ok" || !storeUrl) return null;

  const handleUpdate = () => {
    void openStore(storeUrl);
  };

  if (status === "force_update") {
    return (
      <ForceUpdateModal
        latestVersion={latestVersion}
        currentVersion={currentVersion}
        releaseNotes={releaseNotes}
        onUpdate={handleUpdate}
      />
    );
  }

  // Soft update — session-only dismiss (reappears every app open)
  if (dismissed) return null;

  return (
    <SoftUpdateOverlay
      latestVersion={latestVersion}
      currentVersion={currentVersion}
      releaseNotes={releaseNotes}
      onUpdate={handleUpdate}
      onDismiss={() => setDismissed(true)}
    />
  );
};

/* ─── Soft Update: Full-Screen Overlay ─── */

interface SoftUpdateOverlayProps {
  latestVersion: string | null;
  currentVersion: string | null;
  releaseNotes: string | null;
  onUpdate: () => void;
  onDismiss: () => void;
}

const SoftUpdateOverlay: React.FC<SoftUpdateOverlayProps> = ({
  latestVersion,
  currentVersion,
  releaseNotes,
  onUpdate,
  onDismiss,
}) => (
  <div
    className="fixed inset-0 z-[9998] flex items-center justify-center p-6"
    dir="rtl"
    role="dialog"
    aria-modal="true"
    aria-labelledby="soft-update-title"
    style={{
      background: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    }}
  >
    <div className="w-full max-w-sm flex flex-col items-center text-center">
      {/* Pulsing icon */}
      <div className="relative mb-5 flex items-center justify-center">
        <div className="absolute h-[88px] w-[88px] rounded-full border-2 border-primary/15 animate-[pulse-ring_2s_ease-out_infinite]" />
        <div className="h-[72px] w-[72px] rounded-full bg-primary/10 flex items-center justify-center">
          <ArrowDownToLine className="h-8 w-8 text-primary" />
        </div>
      </div>

      {/* Title */}
      <h2
        id="soft-update-title"
        className="text-[22px] font-extrabold text-foreground mb-1.5"
      >
        גרסה חדשה זמינה
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        גרסה חדשה כוללת שיפורים חשובים
      </p>

      {/* Release notes card */}
      {releaseNotes && (
        <div className="w-full bg-card border border-border rounded-2xl p-4 mb-5 text-right">
          <div className="text-xs font-bold text-muted-foreground tracking-wide mb-2.5">
            מה חדש
          </div>
          <div className="text-sm text-foreground leading-7 whitespace-pre-wrap">
            {releaseNotes}
          </div>
        </div>
      )}

      {/* Version comparison */}
      {(currentVersion || latestVersion) && (
        <div
          className="flex items-center justify-center gap-4 mb-6"
          dir="ltr"
        >
          {currentVersion && (
            <div className="text-center">
              <div className="text-xs text-muted-foreground" dir="rtl">
                גרסתך
              </div>
              <div className="font-mono text-sm font-bold text-muted-foreground mt-0.5">
                {currentVersion}
              </div>
            </div>
          )}
          {currentVersion && latestVersion && (
            <div className="text-lg text-border">→</div>
          )}
          {latestVersion && (
            <div className="text-center">
              <div className="text-xs text-muted-foreground" dir="rtl">
                חדשה
              </div>
              <div className="font-mono text-sm font-bold text-primary mt-0.5">
                {latestVersion}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CTA button */}
      <button
        type="button"
        onClick={onUpdate}
        className="w-full bg-primary text-primary-foreground rounded-2xl py-4 text-base font-bold shadow-[0_4px_16px_hsla(14,100%,64%,0.35)] transition-transform active:scale-[0.98] mb-3"
      >
        עדכון עכשיו
      </button>

      {/* Dismiss */}
      <button
        type="button"
        onClick={onDismiss}
        className="text-xs text-muted-foreground transition-colors hover:text-muted-foreground"
      >
        הזכירו לי מאוחר יותר
      </button>
    </div>
  </div>
);

/* ─── Force Update: Blocking Modal ─── */

interface ForceUpdateModalProps {
  latestVersion: string | null;
  currentVersion: string | null;
  releaseNotes: string | null;
  onUpdate: () => void;
}

const ForceUpdateModal: React.FC<ForceUpdateModalProps> = ({
  latestVersion,
  currentVersion,
  releaseNotes,
  onUpdate,
}) => {
  const handleQuit = () => {
    if (Capacitor.isNativePlatform()) {
      void CapApp.exitApp();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-background/95 backdrop-blur-sm flex items-center justify-center p-6"
      dir="rtl"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
    >
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl text-center space-y-4">
        <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <ArrowDownToLine className="h-7 w-7 text-primary" />
        </div>
        <div className="space-y-2">
          <h2
            id="app-update-title"
            className="text-xl font-bold text-foreground"
          >
            נדרש עדכון אפליקציה
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            הגרסה שמותקנת אצלך ישנה מדי ולא נתמכת. נא לעדכן כדי להמשיך
            להשתמש באפליקציה.
          </p>
        </div>

        {(currentVersion || latestVersion) && (
          <div className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2 flex justify-between gap-4">
            {currentVersion && (
              <span>
                גרסתך:{" "}
                <span className="font-mono font-semibold text-foreground">
                  {currentVersion}
                </span>
              </span>
            )}
            {latestVersion && (
              <span>
                החדשה:{" "}
                <span className="font-mono font-semibold text-primary">
                  {latestVersion}
                </span>
              </span>
            )}
          </div>
        )}

        {releaseNotes && (
          <p className="text-sm text-foreground/80 bg-muted rounded-md px-3 py-2 whitespace-pre-wrap">
            {releaseNotes}
          </p>
        )}

        <div className="flex flex-col gap-2 pt-2">
          <button
            type="button"
            onClick={onUpdate}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-3 text-base font-semibold transition-colors"
          >
            לעדכון בחנות
          </button>
          {Capacitor.isNativePlatform() && (
            <button
              type="button"
              onClick={handleQuit}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              סגירת האפליקציה
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

